/**
 * Translates structured activity log data into the viewer's current language.
 * Falls back to the raw English text for old records that don't have structured data.
 *
 * @param {Object} activity - Activity object with `activity` (string) and optional `activityData` (JSON string or object)
 * @param {Function} t - i18next translation function from useTranslation()
 * @returns {string} Translated activity message
 */
export function translateActivity(activity, t) {
  if (!activity) return '--';

  // If no structured data, fall back to raw English text (old records / user comments)
  if (!activity.activityData) {
    return formatLegacyActivity(activity.activity) || '--';
  }

  try {
    const data = typeof activity.activityData === 'string'
      ? JSON.parse(activity.activityData)
      : activity.activityData;

    if (data.type === 'KEYED_MESSAGE') {
      return translateKeyedMessage(data, t, activity.activity);
    }

    if (data.type === 'FIELD_CHANGES') {
      return translateFieldChanges(data.changes, t);
    }
  } catch (e) {
    // JSON parse error or unexpected structure — fall back
  }

  return formatLegacyActivity(activity.activity) || '--';
}

/**
 * Minimal formatting for legacy (pre-structured) activity text.
 * Replaces "submitted" with "Open" for consistency with existing behavior.
 */
function formatLegacyActivity(text) {
  if (!text) return null;
  return text.replace(/submitted/gi, 'Open');
}

/**
 * Translates a keyed message using i18next.
 */
function translateKeyedMessage(data, t, fallback) {
  if (!data.key) return fallback || '--';

  // Build interpolation params: {0: "val1", 1: "val2", ...}
  const params = {};
  if (data.params) {
    data.params.forEach((val, i) => {
      params[String(i)] = val;
    });
  }

  return t(`activity.keyed.${data.key}`, { defaultValue: fallback || data.key, ...params });
}

/**
 * Translates a list of field changes.
 */
function translateFieldChanges(changes, t) {
  if (!changes || changes.length === 0) return '--';

  if (changes.length === 1) {
    return translateSingleChange(changes[0], t);
  }

  // Multiple changes
  const header = t('activity.multiple_fields_changed', 'Change in multiple fields');
  const parts = changes.map(c => translateChangeShort(c, t));
  return `${header}: ${parts.join(', ')}`;
}

/**
 * Translates a single field change (detailed format).
 */
function translateSingleChange(change, t) {
  const fieldLabel = translateFieldName(change.field, t);

  // File/URL field changes
  if (change.fileType) {
    return translateFileChange(change, t);
  }

  switch (change.changeType) {
    case 'UPDATED':
      return t('activity.updated_field', {
        field: fieldLabel,
        oldValue: change.oldValue,
        newValue: change.newValue,
        defaultValue: 'Updated {{field}} from {{oldValue}} to {{newValue}}',
      });
    case 'ADDED':
      return t('activity.added_field', {
        field: fieldLabel,
        value: change.newValue,
        defaultValue: 'Added {{field}}: {{value}}',
      });
    case 'REMOVED':
      return t('activity.removed_field', {
        field: fieldLabel,
        value: change.oldValue,
        defaultValue: 'Removed {{field}}: {{value}}',
      });
    case 'CHANGED':
    default:
      return t('activity.field_changed', {
        field: fieldLabel,
        defaultValue: '{{field}} changed',
      });
  }
}

/**
 * Translates a field change in short format (for multi-change messages).
 */
function translateChangeShort(change, t) {
  const fieldLabel = translateFieldName(change.field, t);

  if (change.fileType) {
    return translateFileChangeShort(change, t);
  }

  if (change.changeType === 'UPDATED' || change.changeType === 'ADDED') {
    const value = change.newValue || change.oldValue;
    if (value) {
      return t('activity.changed_field_to', {
        field: fieldLabel,
        value,
        defaultValue: 'Changed {{field}} to {{value}}',
      });
    }
  }

  return t('activity.field_changed', {
    field: fieldLabel,
    defaultValue: '{{field}} changed',
  });
}

/**
 * Translates a file/URL field change.
 */
function translateFileChange(change, t) {
  const fileType = t(`activity.file_types.${change.fileType}`, change.fileType);
  const oldCount = change.oldCount || 0;
  const newCount = change.newCount || 0;

  if (oldCount === 0 && newCount > 0) {
    return t('activity.added_files', {
      count: newCount,
      fileType,
      defaultValue: 'Added {{count}} {{fileType}}',
    });
  }
  if (oldCount > 0 && newCount === 0) {
    return t('activity.removed_files', {
      count: oldCount,
      fileType,
      defaultValue: 'Removed {{count}} {{fileType}}',
    });
  }
  if (oldCount !== newCount) {
    return t('activity.updated_files_count', {
      fileType,
      oldCount,
      newCount,
      defaultValue: 'Updated {{fileType}} ({{oldCount}} to {{newCount}})',
    });
  }
  return t('activity.updated_files', {
    fileType,
    defaultValue: 'Updated {{fileType}}',
  });
}

/**
 * Translates a file change in short format.
 */
function translateFileChangeShort(change, t) {
  const fileType = t(`activity.file_types.${change.fileType}`, change.fileType);
  const oldCount = change.oldCount || 0;
  const newCount = change.newCount || 0;

  if (oldCount === 0 && newCount > 0) {
    return t('activity.added_files_short', {
      count: newCount,
      fileType,
      defaultValue: 'added {{count}} {{fileType}}',
    });
  }
  if (oldCount > 0 && newCount === 0) {
    return t('activity.removed_files_short', {
      fileType,
      defaultValue: 'removed {{fileType}}',
    });
  }
  return t('activity.updated_files_short', {
    fileType,
    defaultValue: 'updated {{fileType}}',
  });
}

/**
 * Translates a camelCase field name to a human-readable label.
 * Checks for a translation key first, then falls back to camelCase → words conversion.
 */
function translateFieldName(fieldName, t) {
  if (!fieldName) return '';
  const translated = t(`activity.fields.${fieldName}`, '');
  if (translated) return translated;
  // Fallback: camelCase → "camel case"
  return fieldName.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
}
